import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { CityDto, PromotionTaskDto, PromotionTaskStatus } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Label } from '@upme/ui/components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { useState } from 'react';
import { listCitiesOptions } from '../../../features/cities/queries';
import { useCancelPromotionTask, useDonePromotionTask } from '../../../features/promotion-tasks/mutations';
import {
  listPromotionTasksOptions,
  type PromotionTaskListFilters,
} from '../../../features/promotion-tasks/queries';
import { EmptyState } from '../../../shared/components/empty-state';
import { PageHeader } from '../../../shared/components/page-header';
import { Route as PromotionTasksRoute } from './route';

const TASK_STATUS_LABEL: Record<PromotionTaskStatus, string> = {
  NEW: 'Новая',
  DONE: 'Выполнено',
  CANCELLED: 'Отменена',
};

const TASK_STATUS_VARIANT: Record<PromotionTaskStatus, 'default' | 'secondary' | 'outline' | 'destructive'> =
  {
    NEW: 'default',
    DONE: 'secondary',
    CANCELLED: 'destructive',
  };

const CHANNEL_LABEL: Record<string, string> = {
  SOCIAL: 'Соцсети',
  MORNING_DIGEST: 'Утренний дайджест',
  BROADCAST: 'Рассылка',
  ADS: 'Реклама 25 000 показов',
};

const STATUSES: PromotionTaskStatus[] = ['NEW', 'DONE', 'CANCELLED'];
const ANY = '__any__';

function toFilters(search: ReturnType<typeof PromotionTasksRoute.useSearch>): PromotionTaskListFilters {
  const out: PromotionTaskListFilters = {
    status: search.status,
    limit: search.limit,
  };
  if (search.cityId !== undefined) out.cityId = search.cityId;
  if (search.cursor !== undefined) out.cursor = search.cursor;
  return out;
}

export const Route = createFileRoute('/_authenticated/promotion-tasks/')({
  loaderDeps: ({ search }) => ({
    status: search.status,
    cityId: search.cityId,
    cursor: search.cursor,
    limit: search.limit,
  }),
  loader: async ({ context, deps }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(listCitiesOptions()),
      context.queryClient.ensureQueryData(listPromotionTasksOptions(deps)),
    ]);
  },
  component: PromotionTasksPage,
});

function PromotionTasksPage() {
  const search = PromotionTasksRoute.useSearch();
  const navigate = useNavigate();
  const { data: cities } = useSuspenseQuery(listCitiesOptions());
  const { data: list } = useSuspenseQuery(listPromotionTasksOptions(toFilters(search)));

  const cityById = new Map(cities.map((c: CityDto) => [c.id, c]));

  const applyFilter = (
    patch: Partial<{ status: PromotionTaskStatus | undefined; cityId: string | undefined }>,
  ) => {
    navigate({
      to: '/promotion-tasks',
      search: () => {
        const next = { ...search, ...patch };
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(next)) {
          if (v !== undefined && v !== '' && v !== null) out[k] = String(v);
        }
        return out;
      },
    });
  };

  const hasAnyFilter = search.cityId !== undefined || search.status !== 'NEW';

  return (
    <div>
      <PageHeader
        title="Задачи продвижения"
        description="Заявки организаторов на продвижение событий, ожидающие выполнения."
      />

      {/* Filters */}
      <div className="border rounded-md p-4 mb-4 grid gap-4 grid-cols-1 md:grid-cols-3">
        <div>
          <Label className="text-xs">Статус</Label>
          <Select
            value={search.status ?? 'NEW'}
            onValueChange={(v) => applyFilter({ status: v === ANY ? undefined : (v as PromotionTaskStatus) })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {TASK_STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Город</Label>
          <Select
            value={search.cityId ?? ANY}
            onValueChange={(v) => applyFilter({ cityId: v === ANY ? undefined : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Все" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Все</SelectItem>
              {cities.map((c: CityDto) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasAnyFilter && (
          <div className="md:col-span-3 flex items-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: '/promotion-tasks', search: () => ({}) })}
            >
              Сбросить фильтры
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      {list.items.length === 0 && !search.cursor ? (
        <EmptyState
          title="Нет задач"
          description="Задачи продвижения появятся здесь после того, как организаторы подадут заявки."
        />
      ) : (
        <div className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Событие</TableHead>
                <TableHead>Канал</TableHead>
                <TableHead>Город</TableHead>
                <TableHead>Организатор</TableHead>
                <TableHead>Примечание организатора</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.items.map((task) => {
                const cityName = cityById.get(task.event.cityId)?.name;
                return cityName !== undefined ? (
                  <PromotionTaskRow key={task.id} task={task} cityName={cityName} />
                ) : (
                  <PromotionTaskRow key={task.id} task={task} />
                );
              })}
            </TableBody>
          </Table>

          {list.nextCursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() =>
                  navigate({
                    to: '/promotion-tasks',
                    search: () => {
                      const out: Record<string, string> = {};
                      for (const [k, v] of Object.entries(search)) {
                        if (k === 'cursor') continue;
                        if (v !== undefined && v !== '' && v !== null) out[k] = String(v);
                      }
                      out.cursor = list.nextCursor as string;
                      return out;
                    },
                  })
                }
              >
                Загрузить ещё
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PromotionTaskRow({ task, cityName }: { task: PromotionTaskDto; cityName?: string }) {
  const done = useDonePromotionTask(task.id);
  const cancel = useCancelPromotionTask(task.id);
  const [resultNote, setResultNote] = useState('');
  const [expanded, setExpanded] = useState(false);

  const isNew = task.status === 'NEW';

  return (
    <TableRow>
      <TableCell className="max-w-[220px]">
        <div className="truncate font-medium">{task.event.title}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {new Date(task.event.startsAt).toLocaleDateString('ru-RU')}
        </div>
      </TableCell>
      <TableCell>{CHANNEL_LABEL[task.channel] ?? task.channel}</TableCell>
      <TableCell>{cityName ?? '—'}</TableCell>
      <TableCell className="text-sm">
        <div>
          {task.requestedBy.username ? `@${task.requestedBy.username}` : (task.requestedBy.firstName ?? '—')}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {task.requestedBy.energyAvailable} ⚡
        </div>
      </TableCell>
      <TableCell className="max-w-[200px]">
        <div className="truncate text-sm text-muted-foreground">{task.organizerNote ?? '—'}</div>
      </TableCell>
      <TableCell>
        <Badge variant={TASK_STATUS_VARIANT[task.status]}>{TASK_STATUS_LABEL[task.status]}</Badge>
        {task.resultNote && (
          <div className="text-xs text-muted-foreground mt-1 max-w-[160px] truncate">{task.resultNote}</div>
        )}
      </TableCell>
      <TableCell className="text-right">
        {isNew ? (
          expanded ? (
            <div className="flex flex-col gap-2 items-end min-w-[240px]">
              <Input
                className="text-sm"
                placeholder="Ссылка на пост / причина отмены"
                value={resultNote}
                onChange={(e) => setResultNote(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  disabled={done.isPending}
                  onClick={() => {
                    const note = resultNote.trim();
                    done.mutate(note ? { resultNote: note } : {});
                  }}
                >
                  Выполнено
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={cancel.isPending || resultNote.trim().length === 0}
                  onClick={() => cancel.mutate({ resultNote: resultNote.trim() })}
                >
                  Отменить (возврат энергии)
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setExpanded(false)}>
                  Закрыть
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setExpanded(true)}>
              Обработать
            </Button>
          )
        ) : null}
      </TableCell>
    </TableRow>
  );
}
